/**
 * English (en) translations.
 *
 * Same key shape as the Slovak source dictionary (`Dictionary` = `typeof sk`).
 * TypeScript will flag any missing or extra key.
 *
 * "Solandio" is treated as an indeclinable brand name — never altered.
 *
 * DUAL-MODE COPY: like the Slovak source, this dictionary ships BOTH the warm
 * family-mode wording and the co-parenting `*Separated` variants. The bare keys
 * default to the family-mode (warm) copy. ZERO legal vocabulary in family mode —
 * court / custody-swap / handover / settlement / 50/50 appear ONLY in the
 * explicit `*Separated` strings.
 *
 * Do NOT change the `Dictionary` shape — only translate values.
 */
import { type Dictionary } from './sk';

export const en: Dictionary = {
  appName: 'Solandio',
  tagline: 'A sunny place for your children and family.',

  language: {
    label: 'Language',
    select: 'Choose language',
  },

  common: {
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    add: 'Add',
    edit: 'Edit',
    delete: 'Delete',
    remove: 'Remove',
    close: 'Close',
    loading: 'Loading…',
    none: 'None',
    optional: 'optional',
    back: 'Back',
    today: 'Today',
    note: 'Note',
    required: 'This field is required.',
    error: 'Something went wrong.',
    retry: 'Try again',
    confirmDelete: 'Remove this for good?',
    skipToContent: 'Skip to content',
  },

  auth: {
    signInTitle: 'Welcome to Solandio',
    signInLead:
      'A calm, tidy place where the whole family stays organised — calendar, children, shopping and costs.',
    signInMicrosoft: 'Sign in or sign up',
    signInGoogle: 'Sign in with Google',
    signInMock: 'Sign in (local development)',
    signOut: 'Sign out',
    signedInAs: 'Signed in as',
    loadingSession: 'Checking your session…',
    warmingUp:
      'Getting Solandio ready… the first time you open it after a while, this can take a few seconds.',
    devNotice:
      'Local development mode — no Azure. Your data is stored only in your browser.',
  },

  nav: {
    home: 'Home',
    children: 'Children',
    contacts: 'Contacts',
    health: 'Health',
    calendar: 'Calendar',
    shopping: 'Shopping',
    documents: 'Documents',
    messages: 'Notes',
    expenses: 'Costs',
    agreements: 'Agreements',
    sharing: 'Family',
    audit: 'Audit',
    settings: 'Settings',
    modules: 'Modules',
    menu: 'Menu',
  },

  settings: {
    title: 'Settings',
    lead: 'Manage your profile, language and sign-in.',
    profile: 'Profile',
    displayName: 'Display name',
    displayNameHint: 'This is the name other family members see.',
    email: 'Email',
    emailHint: 'Your email can’t be changed — it’s tied to your sign-in.',
    nameInvalid: 'Enter a name (1–120 characters).',
    saved: 'Saved.',
    preferences: 'Preferences',
    account: 'Account',
    signOutHint: 'Sign out of this device.',
  },

  profileSetup: {
    title: 'Welcome to Solandio!',
    lead: 'Before we begin, tell us what we should call you.',
    nameLabel: 'What should we call you?',
    nameHint: 'Other family members will see this name. You can change it anytime in Settings.',
    namePlaceholder: 'e.g. Jane',
    nameInvalid: 'Please enter a name (1–120 characters).',
    submit: 'Continue',
  },

  family: {
    switcher: 'Family',
    create: 'Create a family',
    createTitle: 'Create your family',
    createLead:
      'A family is your shared space — for your partner, your children and the grandparents. You decide who sees what.',
    nameLabel: 'Family name',
    namePlaceholder: 'e.g. Our family',
    empty: 'You don’t have a family yet.',
  },

  home: {
    /** Greeting; {name} is the signed-in member's first name. */
    greetingMorning: 'Good morning, {name}',
    greetingDay: 'Hello, {name}',
    greetingEvening: 'Good evening, {name}',
    /** Fallback greeting when no name is known yet. */
    greetingNeutral: 'Welcome home',
    add: 'Add',
    /** Section titles on the at-a-glance dashboard. */
    todayTitle: 'Today',
    childrenTitle: 'Children',
    shoppingTitle: 'Shopping',
    expensesTitle: 'Household costs',
    lastNoteTitle: 'Latest note',
    /** Small links/summaries under sections. */
    tomorrow: 'Tomorrow',
    tomorrowEvents: 'Tomorrow: {count} events',
    viewAll: 'View all',
    nothingToday: 'Nothing in the calendar today.',
    noChildrenYet: 'No child here yet.',
    noShoppingYet: 'No shopping list yet.',
    noExpensesYet: 'No costs this month yet.',
    noNotesYet: 'No notes yet.',
    thisMonth: 'This month',
    /** Expenses summary line, e.g. "3 nevyrovnané". */
    outstandingCount: '{count} outstanding',
    /** Empty state — brand-new family with nothing added yet. */
    emptyTitle: 'Welcome home 👋',
    emptyLead: 'Three quick steps and your whole family is in one place.',
    emptyAddChild: 'Add your first child',
    emptyInvitePartner: 'Invite your partner',
    emptyCreateList: 'Create a shopping list',
  },

  children: {
    title: 'Children',
    add: 'Add a child',
    addTitle: 'Add a child',
    none: 'No child here yet.',
    noneLead: 'Add your first child — birthdays, health and contacts all at your fingertips.',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. Emma',
    emojiLabel: 'Avatar (emoji)',
    birthDateLabel: 'Date of birth',
    ageYears: 'years',
    selectPrompt: 'Choose a child above.',
    /** Kid-profile destination: labels for the per-child quick links. */
    profileTitle: 'Child profile',
    profileLead: 'Everything about your child in one place — one click away.',
    quickHealth: 'Health',
    quickContacts: 'Contacts',
    quickCalendar: 'Calendar',
    quickDocuments: 'Documents',
    birthday: 'Birthday',
    ageLabel: 'Age',
    noBirthDate: 'No date of birth filled in.',
    editTitle: 'Edit child',
    removeConfirm: 'Really remove this child? Their records will be kept.',
  },

  contacts: {
    title: 'Contacts',
    add: 'Add a contact',
    empty: 'No contacts yet.',
    typeLabel: 'Type',
    labelLabel: 'Name',
    labelPlaceholder: 'e.g. Dr Novak',
    phoneLabel: 'Phone',
    addressLabel: 'Address',
    types: {
      doctor: 'Doctor',
      school: 'School',
      dentist: 'Dentist',
      activity: 'Activity',
      other: 'Other',
    },
  },

  health: {
    title: 'Health record',
    sensitive: 'Sensitive data (GDPR Art. 9)',
    lead: 'Important health information in one place — for emergencies.',
    allergies: 'Allergies',
    medications: 'Medications',
    insurance: 'Insurance',
    bloodType: 'Blood type',
    conditions: 'Diagnoses / conditions',
    notes: 'Notes',
    updatedAt: 'Last updated',
    placeholderNone: 'Not filled in',
  },

  calendar: {
    title: 'Calendar',
    add: 'Add an event',
    empty: 'No events yet.',
    upcoming: 'Coming up',
    titleLabel: 'Event title',
    typeLabel: 'Type',
    startLabel: 'Start',
    endLabel: 'End',
    allDayLabel: 'All day',
    locationLabel: 'Location',
    /** Separated-mode only: the custody sub-view tab label. */
    custodyView: 'Shared custody',
    /**
     * Event-type labels. `types` is the family-mode default (no handover/custody
     * surfaced). `typesSeparated` adds the co-parenting types and is selected
     * when `family.mode === 'separated'`. Both carry every key so callers can
     * index either map by the same `CalendarEventType`.
     */
    types: {
      appointment: 'Appointment',
      handover: 'Other',
      custody: 'Other',
      school: 'School',
      activity: 'Activity',
      other: 'Other',
    },
    typesSeparated: {
      appointment: 'Appointment',
      handover: 'Handover',
      custody: 'Changeover',
      school: 'School',
      activity: 'Activity',
      other: 'Other',
    },
  },

  custody: {
    title: 'Custody schedule',
    none: 'No custody schedule set up yet.',
    patternLabel: 'Pattern',
    startLabel: 'Schedule start',
    notesLabel: 'Notes',
    set: 'Set up schedule',
    update: 'Edit schedule',
    parentA: 'Parent A',
    parentB: 'Parent B',
    thisWeek: 'This week',
    patterns: {
      'week-week': 'Week / week',
      '2-2-3': '2-2-3',
      custom: 'Custom',
    },
  },

  documents: {
    title: 'Documents',
    sensitive: 'Sensitive',
    add: 'Add a document',
    empty: 'No documents yet.',
    nameLabel: 'Name',
    categoryLabel: 'Category',
    fileLabel: 'File',
    fileHint: 'In local mode the file isn’t uploaded — only the metadata is saved.',
    uploadedBy: 'Uploaded by',
    size: 'Size',
  },

  shopping: {
    title: 'Shopping',
    /** List picker + creation. */
    listLabel: 'List',
    newList: 'New list',
    newListTitle: 'New shopping list',
    listNameLabel: 'List name',
    listNamePlaceholder: 'e.g. Groceries',
    renameList: 'Rename list',
    deleteList: 'Delete list',
    deleteListConfirm: 'Really delete this list and all its items?',
    noLists: 'No list yet.',
    noListsLead: 'Create your first list — for example “Groceries” or “Toiletries”.',
    /** Item add + list. */
    addItem: 'Add an item',
    addItemPlaceholder: 'Add an item…',
    itemNameLabel: 'Item',
    empty: 'The list is empty.',
    emptyLead: 'Add your first item above.',
    /** Category grouping (free-form label on each item). */
    categoryLabel: 'Category',
    categoryNone: 'No category',
    categoryPlaceholder: 'e.g. Fruit and veg',
    /** Assignee = the member responsible for buying the item. */
    assigneeLabel: 'Who buys it',
    assigneeNone: 'Anyone',
    /** Buy-off. */
    bought: 'Bought',
    markBought: 'Mark as bought',
    clearBought: 'Clear bought',
    /** {count} → number of checked items to clear. */
    clearBoughtCount: 'Clear bought ({count})',
    clearBoughtConfirm: 'Clear all bought items from this list?',
    itemCount: '{count} items',
  },

  messages: {
    /**
     * "Notes" in family mode (warm) / "Message log" in separated (court-log).
     * Bare `title`/`lead`/`empty` default to the family wording; the `*Family` and
     * `*Separated` variants let the mode-aware UI pick explicitly. Under the hood
     * messages stay append-only in BOTH modes.
     */
    title: 'Notes',
    titleFamily: 'Notes',
    titleSeparated: 'Message log',
    lead: 'Short family notes and reminders — for example “Pick Emma up at 3pm”.',
    leadFamily: 'Short family notes and reminders — for example “Pick Emma up at 3pm”.',
    leadSeparated:
      'A tamper-proof, court-admissible record of your communication. Messages can’t be edited or deleted.',
    placeholder: 'Write a note…',
    placeholderSeparated: 'Write a message…',
    send: 'Send',
    empty: 'No notes yet. Write the first one.',
    emptyFamily: 'No notes yet. Write the first one.',
    emptySeparated: 'The log is empty for now. Write the first message.',
    newest: 'Newest',
    /** Shown only in separated mode (the append-only guarantee matters there). */
    appendOnlyNote: 'Append-only — no edits, no deletions.',
  },

  expenses: {
    /**
     * "Household costs" (household budgeting) in family mode vs "Costs"
     * with split/settle machinery in separated. Bare keys default to family
     * wording; `*Separated` variants carry the co-parenting framing.
     */
    title: 'Household costs',
    titleFamily: 'Household costs',
    titleSeparated: 'Costs',
    lead: 'An overview of who paid for what — your shared household budget.',
    leadSeparated: 'Shared costs for the children and how they’re split between parents.',
    add: 'Add a cost',
    empty: 'No costs yet.',
    amountLabel: 'Amount',
    categoryLabel: 'Category',
    descriptionLabel: 'Description',
    childLabel: 'Child',
    childAll: 'Whole family',
    incurredOnLabel: 'Date',
    /** Split UI is hidden in family mode; these labels surface in separated. */
    splitLabel: 'Split',
    split5050: 'In half (50/50)',
    splitCustom: 'Custom',
    payerShare: 'Your share',
    otherShare: 'Other parent’s share',
    /** Family mode shows only "kto zaplatil" without any split. */
    paidBy: 'Paid by',
    paidByFamily: 'Paid by',
    settled: 'Settled',
    outstanding: 'Outstanding',
    settle: 'Mark as settled',
    totalOutstanding: 'Total outstanding',
    /** Family-mode month total (no settlement concept). */
    totalMonth: 'Total this month',
    you: 'You',
  },

  agreements: {
    title: 'Agreements',
    lead:
      'Written agreements between parents. One parent proposes an agreement, the other accepts or declines it. An accepted agreement is a tamper-proof, court-admissible record.',
    propose: 'Propose an agreement',
    proposeTitle: 'Propose an agreement',
    proposeLead:
      'State the agreement clearly. The other parent will be able to accept or decline it.',
    proposeNote:
      'Once accepted, the agreement is locked and can no longer be edited or deleted.',
    titleLabel: 'Agreement title',
    titlePlaceholder: 'e.g. Summer holidays 2026',
    bodyLabel: 'Agreement text',
    bodyPlaceholder: 'Describe what you’ve agreed on…',
    empty: 'No agreements yet.',
    emptyLead: 'Propose your first agreement to the other parent.',
    proposedBy: 'Proposed by',
    respondedBy: 'Handled by',
    accept: 'Accept',
    decline: 'Decline',
    withdraw: 'Withdraw',
    locked: 'Locked — no further changes.',
    actionStale: 'This agreement has already been handled in the meantime. We’ve refreshed the list.',
    you: 'You',
    status: {
      proposed: 'Proposed',
      accepted: 'Accepted',
      declined: 'Declined',
      withdrawn: 'Withdrawn',
    },
  },

  sharing: {
    title: 'Family',
    lead:
      'See who belongs to your family and decide what each person sees. You can invite a grandma or grandpa as a trusted person — they only see what you allow.',
    familyNameLabel: 'Family name',
    rename: 'Rename',
    renameTitle: 'Rename family',
    members: 'Family members',
    invite: 'Invite',
    inviteMember: 'Invite member',
    inviteTitle: 'Invite a family member',
    emailLabel: 'Email',
    roleLabel: 'Role in the family',
    roleParent: 'Parent',
    roleParentDesc: 'Full access to everything — for the other parent.',
    roleTrusted: 'Trusted person',
    roleTrustedDesc:
      'A grandma or grandpa, for example — they see only what you tick.',
    scopesLabel: 'What they can see',
    scopesHint:
      'A parent sees everything. A trusted person sees only the areas you tick.',
    parentScopesNote: 'A parent automatically gets full access to every area.',
    sensitiveNote: 'Sensitive — granted only explicitly.',
    seesLabel: 'Sees',
    seesNothing: 'No access to any area yet.',
    fullAccess: 'Full access',
    status: {
      invited: 'Invited',
      active: 'Active',
    },
    you: 'You',
    parentBadge: 'Parent',
    trustedBadge: 'Trusted person',
    changeScopes: 'Edit access',
    removeMember: 'Remove',
    removeConfirm: 'Really remove this person from the family?',
    onlyParents: 'Only parents can manage family members and their access.',
  },

  scopes: {
    children: 'Children',
    calendar: 'Calendar',
    contacts: 'Contacts',
    health: 'Health',
    documents: 'Documents',
    messages: 'Notes',
    expenses: 'Costs',
    shopping: 'Shopping',
  },

  mode: {
    /** Settings → "Family mode" card. Parent-only, lives at the bottom. */
    cardTitle: 'Family mode',
    cardLead:
      'Solandio is set up for a family that lives together. If you live in separate households, you can switch mode — all your data and your children’s whole history stay untouched.',
    /** Current-mode labels. */
    familyLabel: 'One household',
    familyDesc: 'Calendar, children, shopping and costs for the whole family.',
    separatedLabel: 'Separate households',
    separatedDesc:
      'Adds shared custody, cost splitting, agreements and a message log.',
    currentLabel: 'Current mode',
    /** Switch actions. */
    switchToSeparated: 'Switch to separate households',
    switchToFamily: 'Switch back to one household',
    /** Two-step confirmation dialog (deliberate friction). */
    confirmToSeparatedTitle: 'Switch to separate-households mode?',
    confirmToSeparatedLead:
      'We’ll add tools for parents who live apart — shared custody, cost splitting, agreements and a message log.',
    confirmToSeparatedNote:
      'All your data and your children’s whole history stay untouched. You can switch back anytime.',
    confirmToSeparatedAction: 'Yes, switch',
    confirmToFamilyTitle: 'Switch back to one household?',
    confirmToFamilyLead:
      'The separate-households tools will be hidden. Your records stay saved — next time you switch, you’ll find them just as they were.',
    confirmToFamilyAction: 'Yes, switch back',
    /** Shown while the change is being saved + after it lands. */
    switching: 'Switching mode…',
    switched: 'Mode changed.',
    /** Gate copy when a non-parent somehow reaches the card. */
    onlyParents: 'Only parents can change the family mode.',
  },

  audit: {
    title: 'Audit',
    lead: 'Who changed what, and when. The record is tamper-proof (append-only).',
    empty: 'No records yet.',
    onlyParents: 'Only parents can see the audit.',
  },

  errors: {
    forbidden: 'You don’t have permission for this area.',
    notFound: 'Item not found.',
    generic: 'The action failed. Please try again.',
  },

  tour: {
    /** Re-invoke action (Settings + help button in the top bar). */
    start: 'Start the tour',
    help: 'Tour',
    helpHint: 'Replay the quick app walkthrough anytime.',
    /** Joyride control labels. */
    back: 'Back',
    next: 'Next',
    last: 'Done',
    skip: 'Skip',
    close: 'Close',
    /** "Step 2 of 6" — {step} and {steps} are filled in by Joyride. */
    progress: 'Step {step} of {steps}',
    steps: {
      welcomeTitle: 'Welcome to Solandio',
      welcome: 'We’ll show you around in a few seconds.',
      childrenTitle: 'Children',
      children: 'Add and switch between children here.',
      modulesTitle: 'Everything about your family',
      modules:
        'On the left you’ll find everything for your family — calendar, children, health, shopping, costs, documents and notes.',
      familyTitle: 'Family',
      family:
        'Invite your partner or a grandma/grandpa here, and set what they can see.',
      settingsTitle: 'Settings',
      settings: 'Change your name and language in Settings.',
      finishTitle: 'All set!',
      finish: 'Start by adding a child or a shopping list.',
    },
  },
} as const;
